import { inject, Injectable } from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {APIRequestResources, CachedAPIRequest, LoadingService, PaginationResponse} from "../../../core";
import {Router} from "@angular/router";
import { BehaviorSubject, catchError, finalize, Subject, tap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { GetMachineDTO, ImpressionDTO, MachineCurrentShiftDTO, MachineOutputDataDTO, OperatorDashboardCurrentJobDTO, OperatorShiftReportDTO, ShiftJobDetailsReportDTO, Time, TimeDTO } from '../interface/machine.entity';
import { handleError } from 'src/app/core/utils/utils';
import { MachineChangeDTO } from '../interface/jop-operation.entity';


@Injectable({
  providedIn: 'root'
})
export class MachineService extends CachedAPIRequest {

      loading = inject(LoadingService);
      private refreshTrigger = new Subject<void>();
  
      $all = new BehaviorSubject<GetMachineDTO[]>([]);
      all = toSignal(this.$all, {initialValue: []});
  
      $active = new BehaviorSubject<any | undefined>(undefined);
      active = toSignal(this.$active, {initialValue: undefined});
  
      private readonly $statistics = new BehaviorSubject<any>(undefined)
      stat = toSignal(this.$statistics, {initialValue: undefined})

  constructor(protected override http: HttpClient, private router: Router) {
    super(http, APIRequestResources.MachineService )
  }

create = (jobOperation: any) => {
    return this.post<any>(jobOperation, {}).pipe(
    );
}

getById = (id: string, refresh = true) => {
    return this.get<GetMachineDTO>({id}, refresh ? 'freshness' : 'performance')
        .pipe(tap((res) => this.$active.next(res.data)),);
}

getAll(refresh = true) {
    return this.get<GetMachineDTO[]>({}, refresh ? 'freshness' : 'performance')
      .pipe(
        tap(res => this.$all.next(res.data)),
        catchError(handleError)
      )
  }

  getNextAvailableTime (id: number, refresh = true) {
    return this.get<Time>({endpoint:`next-available-time/${id}`},  refresh ? 'freshness' : 'performance').pipe()
  }


triggerRefresh() {
    this.refreshTrigger.next();
}

getRefreshObservable() {
    return this.refreshTrigger.asObservable();
}

 getDailyImpressions(id:number, searchParams:any, refresh = true) {
    return this.get<ImpressionDTO>({endpoint: `impressions/${id}`, params: searchParams }, refresh ? 'freshness' : 'performance')
    .pipe(tap((res) => this.$active.next(res.data)),);
  }

  getDailyOperationTime(id:number, searchParams:any, refresh = true) {
    return this.get<TimeDTO>({endpoint: `timeline/${id}`, params: searchParams }, refresh ? 'freshness' : 'performance')
    .pipe(tap((res) => this.$active.next(res.data)),);
  }

  machineChange(id:number, machineChangeDTO: MachineChangeDTO) {
    return this.put<any>(machineChangeDTO, {endpoint: `operation/${id}`
        }).pipe(

        )
  }

  getDailyChartData(id:number, searchParams:any, refresh = true) {
    return this.get<MachineOutputDataDTO>({endpoint: `output/${id}`, params: searchParams }, refresh ? 'freshness' : 'performance')
    .pipe(tap((res) => this.$active.next(res.data)),);
  }

  getMachineCurrentShifts(id:number, refresh = true) {
    return this.get<MachineCurrentShiftDTO>({endpoint: `current-shift/${id}` }, refresh ? 'freshness' : 'performance')
    .pipe(tap((res) => this.$active.next(res.data)),);
  }

  getOperatorDashboardCurrentJob(id: number, refresh = true) {
    return this.get<OperatorDashboardCurrentJobDTO>({endpoint: `operator-dashboard/${id}` }, refresh ? 'freshness' : 'performance')
    .pipe(tap((res) => this.$active.next(res.data)),);
  }

  getDailyReport(id: number, searchParams: any, refresh = true) {
    return this.get<any>({ endpoint: `daily/report/${id}`, params: searchParams }, refresh ? 'freshness' : 'performance')
      .pipe(tap((res) => this.$active.next(res.data)));
  }

  getMonthlyReport(id: number, searchParams: any, refresh = true) {
    return this.get<any>({ endpoint: `monthly/report/${id}`, params: searchParams }, refresh ? 'freshness' : 'performance')
      .pipe(tap((res) => this.$active.next(res.data)));
  }

  getOperationSchedule(id: number, searchParams: { start_date: string, end_date: string, job_status?: string }, refresh = true) {
    return this.get<any>({ endpoint: `operations/schedule/${id}`, params: searchParams }, refresh ? 'freshness' : 'performance')
      .pipe(tap((res) => this.$active.next(res.data)));
  }

  getOperatorShiftReport( machineId: number, searchParams: { year: number; month: number }, refresh = true ) {
    return this.get<OperatorShiftReportDTO[]>(
      {
        endpoint: `operator/shift/report/${machineId}`,
        params: searchParams
      },
      refresh ? 'freshness' : 'performance'
    ).pipe(
      tap((res) => this.$active.next(res.data))
    );
  }

  getShiftJobDetailsReport( id: number, searchParams: { year: number; month: number }, refresh = true ) {
    return this.get<ShiftJobDetailsReportDTO[]>(
      {
        endpoint: `shift/job/details/report/${id}`,
        params: searchParams
      },
      refresh ? 'freshness' : 'performance'
    ).pipe(
      tap((res) => this.$active.next(res.data))
    );
  }

}
